<template>
  <div class="app-theme-editor grid">
    <slot></slot>
    <div class="app-theme-editor__header col-12">
        <p class="element-badge is--success" v-if="message">{{ message }}</p>
        <p v-else-if="errors.length">
            <span v-for="(error, index) in errors" :key="`error-${index}`" class="element-badge is--error">{{ error }}</span>
        </p>
        <div class="page-main__buttons">
            <button v-if="canSync"
                type="button"
                class="element-button--secondary element-button"
                @click="syncTheme(source)"
                :disabled="isSyncing">
                {{ syncButtonLabel }}
            </button>
            <button 
                type="button"
                class="element-button--primary element-button"
                @click="submit()"
                :disabled="status">
                {{ status === 'saving' ? 'Saving...' : 'Save' }}
            </button>
        </div>
    </div>
    <div class="app-theme-editor__sections col-12 col-md-6 col-lg-4">
        <div class="app-theme-editor__theme page-main__section">
            <div class="page-main__section-header">
                <h3 class="page-main__section-title">Theme</h3>
                <p class="element-instructions">Select the general design of your project.</p>
            </div>
            <!-- <div v-if="hasLogo">
                <h3>Logo</h3>
                <div class="theme-logo shadow logo--client" v-if="logo">
                    <img :src="logo">
                    <button class="close" @click="logo = null">&times;</button>
                </div>
                <p class="text-muted" v-else>No logo specified.</p>
                <button type="button" class="btn btn-outline-secondary"
                    data-toggle="modal"
                    data-target="#asset-manager-modal"
                    data-backdrop="static">
                    Set Logo
                </button>
                <hr>
            </div> -->

            <div class="input select">
                <label>Theme</label>
                <span v-if="isPhoenix">
                    <select class="form-control" name="theme" id="theme" v-model="theme" v-if="themeDropdownOptions.length">
                        <option v-for="(label, key) in themeDropdownOptions" :key="key" :value="label.id" >
                            {{ label.name }}
                        </option>  
                    </select>
                    <input type="text" class="form-control" name="theme" id="theme" placeholder="i.e. default" v-model="theme" v-else>
                </span>
                <input type="text" class="form-control" name="theme" id="theme" placeholder="i.e. default" v-model="theme" v-else>
            </div>
            <div class="input select" v-if="isPhoenix">
                <label>Color Mode</label>
                <select class="form-control" name="color_mode" v-model="color_mode">
                    <option v-for="(label, key) in color_mode_dropdown_array" :key="key" :value="key" >
                        {{ label }}
                    </option>
                </select> 
            </div>
        </div>
        <div v-if="projectGlobalSharedSettingsUrl" class="app-theme-editor__icons page-main__section">
            <div class="page-main__section-header">
                <h3 class="page-main__section-title">Icons and Logo</h3>
                <p class="element-instructions">To replace the icons that are used in this project or to add a logo to the navigation, go to the 
                    <a class="element-link" :href="projectGlobalSharedSettingsUrl">Global Data Shared Settings</a>
                    page.
                </p>
            </div>
        </div>
        <div v-if="canSync" class="app-theme-editor__variables-portal page-main__section">
            <div class="app-theme-editor__variables-portal-header page-main__section-header">
                <h3 class="page-main__section-title">Portal Variables</h3>
                <p class="element-instructions">Define variables inherited from your Portal integration.</p>
            </div>
            <div v-if="portalVariables && portalVariables.length">
                <div v-for="(variable, index) in portalVariables" :key="`themevars-${index}`" class="app-theme-editor__variables-list">
                    <div class="app-theme-editor__variables-name input text">
                        <input type="text" v-model="portalVariables[index].name" placeholder="$variable-name" :disabled="status" readonly>
                    </div>
                    <div class="input text">
                        <input type="text" v-model="portalVariables[index].value" placeholder="#f0f0f0" :disabled="status" readonly>
                    </div>
                </div>
            </div>
            <div v-else class="app-theme-editor__variables-empty">No variables defined. Access your Portal integration to add variables.</div>
        </div>
        <div v-if="!isPhoenix" class="app-theme-editor__variables-local page-main__section">
            <div class="app-theme-editor__variables-local-header page-main__section-header">
                <h3 class="page-main__section-title">Local Variables</h3>
                <p class="element-instructions">Define variables to use specific for your project.</p>
                <button class="element-button--secondary element-button" @click.prevent="addVariable()" :disabled="status">Create New Variable</button>
            </div>
            <div v-for="(variable, index) in variables" :key="`themevars-${index}`" class="app-theme-editor__variables-list">
                <div class="app-theme-editor__variables-name input text">
                    <input type="text" v-model="variables[index].name" placeholder="$variable-name" :disabled="status">
                </div>
                <div class="input text">
                    <input type="text" v-model="variables[index].value" placeholder="#f0f0f0" :disabled="status">
                </div>
                <button class="element-button--icon element-button" @click="variables.splice(index, 1)" :disabled="status">
                    <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                </button>
            </div>
        </div>
    </div>
    <div class="app-theme-editor__whitelabel page-main__section col-12 col-md-6 col-lg-8" v-if="allowCustom">
        <div class="app-theme-editor__whitelabel-header page-main__section-header">
            <h4 class="page-main__section-title" v-if="isPhoenix">White Label (CSS)</h4>
            <h4 class="page-main__section-title" v-else>White Label (SCSS)</h4>
            <p class="element-instructions">Customize your selected theme for your project through variable updates in this stylesheet. These styles will be appended to the stylesheet compilation process.</p>
            <app-button @click="openAssets()" class="element-button--secondary element-button">
                Insert Asset URL
            </app-button>
        </div>
        <code-editor ref="style-editor" :value="styles" language="scss" class="text-box" :disabled="status"></code-editor>
    </div>
    <div class="col-md-8" v-else>
        <!-- display something else -->
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import _ from 'lodash'
import { mapState } from 'vuex'
import Echo from '../helpers/echo'
import { MODEL_MAP } from '../helpers/builder'
import axios from 'axios'
import ThemeVariable from './ThemeVariable.vue'
import api from '../api/publisher'

export default {
    components: { ThemeVariable },
    props: {
        url: {
            type: String,
            required: true
        },
        source: {
            type: Object,
            required: true
        },
        globalSharedDataUrl: {
            type: String,
            default: null
        },
        allowCustom: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            variables: _.map(this.source.theme_vars, (value, name) => {
                return { name, value }
            }),
            portalVariables: _.map(this.source.portal_theme_vars, (value, name) => {
                return { name, value }
            }),
            logo: this.source.logo,
            styles: this.source.styles,
            theme: this.source.theme,
            color_mode: this.source.color_mode ?? '',
            color_mode_dropdown_array: {'':'None', 'light':'Light', 'dark':'Dark'},
            message: null,
            errors: [],
            status: null,
            isSyncing: false
        }
    },
    computed: {
        ...mapState('builder', {
            client: state => state.client
        }),
        hasLogo () {
            return this.source.hasOwnProperty('logo')
        },
        canSync () {
            return !!this.client?.integration
        },
        isPhoenix () {
            return this.source.driver?.category === 'phoenix'
        },
        themeDropdownOptions () {
            const dropDownOptions = [
                {
                    id: "default",
                    name: "None (Default)",
                }
            ];
            const currentThemeOptions = this.source.driver?.settings?.themes ?? []
            const result = dropDownOptions.map(option => {
            const found = currentThemeOptions.find(currentOption => currentOption.id === option.id);
                return found ? found : option;
            }).concat(currentThemeOptions.filter(currentOption => !dropDownOptions.some(option => option.id === currentOption.id)));

            return result
        },
        syncButtonLabel () {
            return this.isSyncing ? 'Syncing to Portal...' : 'Sync to Portal'
        },
        projectGlobalSharedSettingsUrl () {
            if (!this.isPhoenix || !this.globalSharedDataUrl)
                return null

            return this.globalSharedDataUrl
        }
    },
    methods: {
        addVariable () {
            this.variables.push({
                name: '',
                value: ''
            })
        },
        openAssets () {
            const $manager = $('#asset-manager-modal')
            $manager.modal('show')
        },
        async submit () {
            this.status = 'saving'

            // generate formdata
            const formData = new FormData()

            formData.append('_method', 'PUT')
            formData.append('theme', this.theme)
            formData.append('styles', this.$refs['style-editor'].getValue())
            if (this.hasLogo) {
                formData.append('logo', this.logo)
            }

            // attach all theme vars
            const variables = _.filter(this.variables, ({ name, value }) => name.length && value.length)
            if (variables.length) {
                _.each(variables, ({ name, value }) => {
                    if (name && value) {
                        formData.append(`theme_vars[${name}]`, value)
                    }
                })
            } else {
                formData.append('theme_vars', [])
            }

            formData.append('color_mode', this.color_mode)
            
            try {
                this.message = null
                const response = await axios.post(this.url, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })

                this.errors = _.get(response.data, 'errors', [])
                if (!this.errors.length) {
                    this.message = 'Design successfully updated.'; 
                }
            } catch (err) {
                console.error(err)
                this.errors = _.get(err, 'response.data.errors', [])
            }

            this.status = null
        },
        async setupAssetManager () {
            const $manager = $('#asset-manager-modal')

            this.$store.commit('assets/setOptions', { assetType: ['image', 'font'] })

            if (!this.$store.state.assets.loaded) {
                await this.$store.dispatch('assets/update')
            }

            $manager.on('show.bs.modal', () => {
                $manager.one('selected', (event, { file }) => {
                    // insert text
                    const value = `"/assets/${file.path}"`
                    this.$refs['style-editor'].insert(value)
                })
            })

            $manager.on('hide.bs.modal', () => {
                $manager.off('selected')
            })
        },
        async syncTheme(project) {
            this.isSyncing = true
            await api.syncTheme(project.id)
        },
        processNotification (notification) {
            console.log(notification)
            if (notification.progress === 1) {
                this.isSyncing = false
                this.driverSyncedAt = notification.model.driver_synced_at
            }
        }
    },
    mounted () {
        this.setupAssetManager()
    },
    created () {
        this.$store.dispatch('builder/initialize', {
            source: this.source,
            type: 'project'
        })

        Echo.private(`${MODEL_MAP['project'].model}.${this.source.id}`)
            .notification((notification) => {
                this.processNotification(notification)
            })
    }
}
</script>
